import { useEffect, useMemo, useState } from "react";
import api from "../api/client";

/**
 * Admin → Roles & access.
 *
 * admin / technician / viewer are coarse. Every MSP has a technician they do
 * not want in Licensing, or a viewer who genuinely does need the reports
 * screen. This screen turns individual features on and off per role; the
 * navigation is built from the result, so a feature a role cannot use is not
 * shown and then 403'd.
 *
 * Administrator is not in the matrix on purpose. There is no combination of
 * clicks that locks the last administrator out of the screen they would need
 * to undo it — the server short-circuits that role before it looks anything
 * up. The column is still drawn, as a permanent tick, because its absence
 * would read as an oversight rather than a decision.
 */

type Feature = {
  key: string;
  label: string;
  group: string;
  description: string;
  routes: string[];
  roles: Record<string, boolean>;
};

const ROLE_LABEL: Record<string, string> = {
  admin: "Admin",
  technician: "Technician",
  viewer: "Viewer",
};

export default function RolesAccess() {
  const [features, setFeatures] = useState<Feature[] | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const load = () =>
    api
      .get("/api/v1/rbac/features")
      .then((r) => {
        setFeatures(r.data.features);
        setRoles(r.data.configurable_roles);
        setDirty({});
      })
      .catch(() => setFeatures([]));

  useEffect(() => {
    load();
  }, []);

  const cellKey = (role: string, key: string) => `${role}::${key}`;

  const valueOf = (f: Feature, role: string) => {
    const k = cellKey(role, f.key);
    return k in dirty ? dirty[k] : f.roles[role];
  };

  const toggle = (f: Feature, role: string) => {
    const k = cellKey(role, f.key);
    const next = { ...dirty };
    const proposed = !valueOf(f, role);
    if (proposed === f.roles[role]) delete next[k];
    else next[k] = proposed;
    setDirty(next);
  };

  const save = async () => {
    setSaving(true);
    setNotice(null);
    try {
      const cells = Object.entries(dirty).map(([k, allowed]) => {
        const [role, feature_key] = k.split("::");
        return { role, feature_key, allowed };
      });
      const r = await api.put("/api/v1/rbac/features", { cells });
      setFeatures(r.data.features);
      setDirty({});
      setNotice({
        kind: "ok",
        text:
          r.data.changed === 0
            ? "Nothing to change."
            : `Saved. ${r.data.changed} change${r.data.changed === 1 ? "" : "s"} applied — people signed in now will see it on their next page load.`,
      });
    } catch (e: any) {
      setNotice({ kind: "err", text: e?.response?.data?.detail || "Could not save" });
    } finally {
      setSaving(false);
    }
  };

  const grouped = useMemo(() => {
    const out: [string, Feature[]][] = [];
    for (const f of features || []) {
      const last = out[out.length - 1];
      if (last && last[0] === f.group) last[1].push(f);
      else out.push([f.group, [f]]);
    }
    return out;
  }, [features]);

  const dirtyCount = Object.keys(dirty).length;

  if (features === null) return <p className="text-sm text-gray-400">Loading…</p>;

  return (
    <div className="max-w-4xl">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">Roles &amp; access</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
        Choose what each role can reach. Anything switched off is hidden from that role's navigation
        and refused by the server, so nobody clicks through to a permission error.
      </p>

      <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/25 p-3 mb-5 text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
        <strong className="text-gray-800 dark:text-gray-100">Administrators always have everything.</strong>{" "}
        That column cannot be edited — otherwise the last administrator could switch off the screen
        they would need to switch it back on. This also does not replace the permission checks on
        each action: it takes access away from a role, it never grants any.
      </div>

      {notice && (
        <div
          className={`mb-4 text-sm px-3 py-2 rounded-lg ${
            notice.kind === "ok"
              ? "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300"
              : "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300"
          }`}
        >
          {notice.text}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Feature
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 w-28">
                Admin
              </th>
              {roles.map((r) => (
                <th
                  key={r}
                  className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 w-28"
                >
                  {ROLE_LABEL[r] || r}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {grouped.map(([group, items]) => (
              <>
                {group && (
                  <tr key={`g:${group}`} className="bg-gray-50/60 dark:bg-gray-900/30">
                    <td
                      colSpan={roles.length + 2}
                      className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500"
                    >
                      {group}
                    </td>
                  </tr>
                )}
                {items.map((f) => (
                  <tr key={f.key} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800 dark:text-gray-100">{f.label}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{f.description}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {/* Drawn, never editable — see the note above the table. */}
                      <span
                        className="text-green-600 dark:text-green-400"
                        title="Administrators always have every feature"
                      >
                        ✓
                      </span>
                    </td>
                    {roles.map((role) => {
                      const on = valueOf(f, role);
                      const changed = cellKey(role, f.key) in dirty;
                      return (
                        <td key={role} className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => toggle(f, role)}
                            role="switch"
                            aria-checked={on}
                            aria-label={`${f.label} for ${ROLE_LABEL[role] || role}`}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                              on ? "bg-brand" : "bg-gray-300 dark:bg-gray-600"
                            } ${changed ? "ring-2 ring-offset-1 ring-amber-400 dark:ring-offset-gray-800" : ""}`}
                          >
                            <span
                              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                                on ? "translate-x-5" : "translate-x-1"
                              }`}
                            />
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3 mt-4">
        <button
          onClick={save}
          disabled={saving || dirtyCount === 0}
          className="bg-brand text-white text-sm px-4 py-2 rounded-lg font-medium disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? "Saving…" : dirtyCount === 0 ? "No changes" : `Save ${dirtyCount} change${dirtyCount === 1 ? "" : "s"}`}
        </button>
        {dirtyCount > 0 && (
          <button
            onClick={() => setDirty({})}
            disabled={saving}
            className="text-sm px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-100"
          >
            Discard
          </button>
        )}
      </div>
    </div>
  );
}
